/**
 * Iter129 · Bitácora de mensajes WhatsApp — panel de diagnóstico en RRHH.
 *
 * Muestra los últimos eventos capturados por el webhook Twilio, incluye
 * respuestas enviadas y motivo de rechazo. Sirve para explicar por qué a
 * un colaborador no le registró la entrada (número mal cargado, mensaje
 * confuso, retry de Twilio, etc.).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChatCircleDots, ArrowClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_META = {
  ok: { label: "Registrado", cls: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  unknown_sender: { label: "Número no encontrado", cls: "bg-rose-100 text-rose-900 border-rose-300" },
  unknown_command: { label: "Comando no reconocido", cls: "bg-amber-100 text-amber-900 border-amber-300" },
  outbound_reminder: { label: "Recordatorio enviado", cls: "bg-blue-100 text-blue-900 border-blue-300" },
};

const ACTION_META = {
  present: "Entrada",
  checkout: "Salida",
  reminder_sent: "Recordatorio 6am",
};

export default function WhatsAppLogTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState("50");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/twilio/whatsapp/events?limit=${limit}`);
      setEvents(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(apiError(e));
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    return events.filter((e) => {
      if (statusFilter !== "all" && e.status !== statusFilter) return false;
      if (!qLower) return true;
      return [
        e.employee_name, e.from_e164, e.body, e.reply, e.action,
      ].some((v) => (v || "").toString().toLowerCase().includes(qLower));
    });
  }, [events, statusFilter, q]);

  const stats = useMemo(() => {
    const counts = { total: events.length, ok: 0, unknown_sender: 0,
                      unknown_command: 0, outbound_reminder: 0 };
    for (const e of events) counts[e.status] = (counts[e.status] || 0) + 1;
    return counts;
  }, [events]);

  return (
    <div className="space-y-4" data-testid="hr-whatsapp-log">
      <div className="bg-gradient-to-br from-emerald-950 via-slate-900 to-slate-800 border border-emerald-800 rounded-lg p-4 text-white">
        <div className="flex items-center gap-2 mb-2">
          <ChatCircleDots size={20} weight="fill" />
          <h3 className="font-heading font-black text-lg">Bitácora de WhatsApp · Asistencia</h3>
        </div>
        <p className="text-[12px] text-emerald-100">
          Cada mensaje inbound/outbound del webhook Twilio queda registrado aquí.
          Úsalo para diagnosticar por qué a un colaborador no le fichó la entrada
          (número mal cargado, mensaje confuso, retry de Twilio, etc.).
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
          {[
            ["Total", stats.total, "bg-white/10"],
            ["OK", stats.ok || 0, "bg-emerald-600/70"],
            ["Sin match", stats.unknown_sender || 0, "bg-rose-600/70"],
            ["Comando ?", stats.unknown_command || 0, "bg-amber-500/70"],
            ["Recordatorios 6am", stats.outbound_reminder || 0, "bg-blue-600/70"],
          ].map(([l, v, cls]) => (
            <div key={l} className={`${cls} rounded p-2`}>
              <div className="text-[10px] uppercase font-bold opacity-80">{l}</div>
              <div className="text-xl font-black">{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-end">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Buscar por nombre, número, cuerpo…"
                 data-testid="wa-log-search"
                 className="pl-7 h-9" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-500 block">Estado</label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-52 h-9" data-testid="wa-log-status"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(STATUS_META).map(([v, m]) => (
                <SelectItem key={v} value={v}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-500 block">Últimos</label>
          <Select value={limit} onValueChange={setLimit}>
            <SelectTrigger className="w-24 h-9" data-testid="wa-log-limit"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {["25", "50", "100", "200"].map((n) => (
                <SelectItem key={n} value={n}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}
                className="h-9 gap-1.5" data-testid="wa-log-refresh">
          <ArrowClockwise size={14} weight="bold" /> Recargar
        </Button>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-xs" data-testid="wa-log-table">
          <thead className="bg-slate-900 text-white uppercase text-[10px]">
            <tr>
              <th className="px-2 py-2 text-left">Recibido</th>
              <th className="px-2 py-2 text-left">De</th>
              <th className="px-2 py-2 text-left">Empleado</th>
              <th className="px-2 py-2 text-left">Mensaje</th>
              <th className="px-2 py-2 text-center">Acción</th>
              <th className="px-2 py-2 text-center">Estado</th>
              <th className="px-2 py-2 text-left">Respuesta</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-center py-4 text-slate-400 italic">Cargando…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-4 text-slate-400 italic">
                Sin eventos que coincidan.
              </td></tr>
            )}
            {filtered.map((e, i) => {
              const meta = STATUS_META[e.status] || { label: e.status || "-", cls: "bg-slate-100 text-slate-700 border-slate-300" };
              return (
                <tr key={e.message_sid || i} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-2 py-2 whitespace-nowrap text-slate-500 font-mono text-[10px]">
                    {(e.received_at || "").slice(0, 19).replace("T", " ")}
                  </td>
                  <td className="px-2 py-2 font-mono text-[10.5px] text-slate-800">{e.from_e164 || "-"}</td>
                  <td className="px-2 py-2 font-semibold text-slate-900">{e.employee_name || <span className="text-slate-400 italic">(sin match)</span>}</td>
                  <td className="px-2 py-2 text-slate-700 max-w-[240px] truncate" title={e.body}>{e.body || "-"}</td>
                  <td className="px-2 py-2 text-center text-slate-600">{ACTION_META[e.action] || "-"}</td>
                  <td className="px-2 py-2 text-center">
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${meta.cls}`}>{meta.label}</span>
                  </td>
                  <td className="px-2 py-2 text-slate-600 max-w-[280px] truncate" title={e.reply}>{e.reply || "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-slate-500 italic">
        Tip: si aparece «Número no encontrado», ve a Nómina → editar colaborador
        y captura el WhatsApp en formato internacional (+52…). El sistema hace
        matching por dígitos, así que espacios y guiones no importan.
      </p>
    </div>
  );
}
